
import Vue from 'vue'
import { apiService } from '@/service/api.service'
import CreateActivationKeyDialog from './CreateActivationKeyDialog.vue'
import SendActivationKeyDialog from './SendActivationKeyDialog.vue'
import AdminTableSubheader from '../shared/AdminTableSubheader.vue'

interface LicenseType {
  id: number;
  label: string;
}

/* eslint-disable camelcase */
interface ActivationKey {
  id: number;
  activation_code: string;
  licence_configurations?: Array<{
    licence_type: string;
    licence_type_id: number;
    quantity: number;
    device_type_restriction: string | null;
  }>;
  licence_type?: string;
  quantity?: number;
  duration_days: number;
  is_used: boolean;
  used_by: string | null;
  created_at: string;
  used_at: string | null;
  expires_at: string | null;
  notes: string | null;
}

interface CreatedKeyResponse {
  activation_code?: string;
  licence_configurations?: Array<{
    licence_type: string;
    quantity: number;
    device_type_restriction: string | null;
  }>;
  licence_type?: string;
  quantity?: number;
  total_quantity?: number;
  duration_days?: number;
  device_type_restriction?: string | null;
  keys?: Array<any>;
  count?: number;
  message?: string;
}
/* eslint-enable camelcase */

export default Vue.extend({
  name: 'ActivationKeys',
  components: { CreateActivationKeyDialog, SendActivationKeyDialog, AdminTableSubheader },
  data () {
    return {
      loading: false,
      creating: false,
      activationKeys: [] as ActivationKey[],
      selectedKeys: [] as ActivationKey[],
      licenseTypes: [] as LicenseType[],
      page: 1,
      itemsPerPage: 10,
      totalItems: 0,
      showUsedKeys: true,
      createDialog: false,
      sendDialog: false,
      successDialog: false,
      createdKey: {} as CreatedKeyResponse,
      headers: [
        { text: 'Activation Code', value: 'activation_code', sortable: false },
        { text: 'License Type', value: 'licence_type' },
        { text: 'Quantity', value: 'quantity' },
        { text: 'Duration (Days)', value: 'duration_days' },
        { text: 'Device Restriction', value: 'device_type_restriction' },
        { text: 'Restricted User', value: 'restricted_user_email' },
        { text: 'Status', value: 'is_used' },
        { text: 'Used By', value: 'used_by' },
        { text: 'Created At', value: 'created_at' },
        { text: 'Used At', value: 'used_at' },
        { text: 'Expires At', value: 'expires_at' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      bulkKeysHeaders: [
        { text: 'Activation Code', value: 'activation_code' },
        { text: 'Licenses', value: 'licence_configurations' },
        { text: 'Duration', value: 'duration_days' }
      ],
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success'
    }
  },
  mounted () {
    this.loadLicenseTypes()
    this.loadActivationKeys()
  },
  methods: {
    async loadLicenseTypes () {
      try {
        const response = await apiService.get('/get_all_licences_info')
        this.licenseTypes = response.data.licences || []
      } catch (error) {
        console.error('Error loading license types:', error)
        this.showSnackbar('Error loading license types', 'error')
      }
    },

    async loadActivationKeys () {
      this.loading = true
      try {
        const response = await apiService.get('/licences/activation-keys/', {
          params: { page: this.page, per_page: this.itemsPerPage, include_used: this.showUsedKeys }
        })
        if (response.data.ok) {
          this.activationKeys = response.data.keys
          this.totalItems = response.data.page_info.total_items
        }
      } catch (error) {
        console.error('Error loading activation keys:', error)
        this.showSnackbar('Error loading activation keys', 'error')
      } finally {
        this.loading = false
      }
    },

    openCreateDialog () { this.createDialog = true },
    openSendDialog () { this.sendDialog = true },

    onBulkSent (results: Array<{ email: string; color: string; message: string }>) {
      const total = results.length
      const success = results.filter((r) => r.color === 'success').length
      const warnings = results.filter((r) => r.color === 'warning').length
      const errors = results.filter((r) => r.color === 'error').length
      let msg = `Sent ${total} key(s): ${success} success`
      if (warnings > 0) msg += `, ${warnings} warning`
      if (errors > 0) msg += `, ${errors} error`
      const color = errors > 0 ? (success > 0 ? 'warning' : 'error') : warnings > 0 ? 'warning' : 'success'
      this.showSnackbar(msg, color)
      this.loadActivationKeys()
    },

    exportSelectedKeys () {
      if (this.selectedKeys.length === 0) { this.showSnackbar('No keys selected', 'warning'); return }
      const exportData = this.selectedKeys.map((key) => {
        const licenseInfo: string[] = []
        if (key.licence_configurations && key.licence_configurations.length > 0) {
          key.licence_configurations.forEach((config) => {
            const dr = config.device_type_restriction ? `(${config.device_type_restriction.toUpperCase()})` : ''
            licenseInfo.push(`${config.quantity}x ${config.licence_type} ${dr}`.trim())
          })
        } else {
          const dr = (key as any).device_type_restriction ? `(${(key as any).device_type_restriction.toUpperCase()})` : ''
          licenseInfo.push(`${key.quantity || 1}x ${key.licence_type || 'N/A'} ${dr}`.trim())
        }
        return {
          activation_code: key.activation_code,
          license_info: licenseInfo.join(', '),
          duration_days: key.duration_days,
          expires_at: key.expires_at ? this.formatDate(key.expires_at) : 'Never',
          notes: key.notes || ''
        }
      })
      const csvRows = [
        ['Activation Code', 'License Info', 'Duration (Days)', 'Expires At', 'Notes'].join(','),
        ...exportData.map((row) => [`"${row.activation_code}"`, `"${row.license_info}"`, row.duration_days, `"${row.expires_at}"`, `"${row.notes}"`].join(','))
      ]
      this._downloadCsv(csvRows.join('\n'), `activation-keys-${new Date().toISOString().split('T')[0]}.csv`)
      this.showSnackbar(`Exported ${this.selectedKeys.length} activation key(s)`, 'success')
      this.selectedKeys = []
    },

    /* eslint-disable camelcase */
    async createActivationKey (payload: {
      licence_configurations: Array<{ licence_type_id: number | null; quantity: number; device_type_restriction: string | null }>;
      duration_days: number;
      expires_at: string | null;
      notes: string;
    }) {
      this.creating = true
      try {
        const response = await apiService.post('/licences/activation-keys/create', payload)
        /* eslint-enable camelcase */
        if (response.data.ok) {
          this.createdKey = response.data.data
          this.successDialog = true
          this.createDialog = false
          this.loadActivationKeys()
        } else {
          this.showSnackbar(response.data.message || 'Error creating activation key', 'error')
        }
      } catch (error) {
        const err = error as { response?: { data?: { message?: string } } }
        this.showSnackbar(err.response?.data?.message || 'Error creating activation key', 'error')
      } finally {
        this.creating = false
      }
    },

    async deleteKey (item: ActivationKey) {
      if (!confirm(`Are you sure you want to delete activation key ${item.activation_code}?`)) return
      try {
        const response = await apiService.delete(`/licences/activation-keys/${item.id}`)
        if (response.data.ok) {
          this.showSnackbar('Activation key deleted successfully', 'success')
          this.loadActivationKeys()
        } else {
          this.showSnackbar(response.data.message || 'Error deleting activation key', 'error')
        }
      } catch (error) {
        const err = error as { response?: { data?: { message?: string } } }
        this.showSnackbar(err.response?.data?.message || 'Error deleting activation key', 'error')
      }
    },

    copyToClipboard (text: string) {
      navigator.clipboard.writeText(text)
        .then(() => this.showSnackbar('Copied to clipboard!', 'success'))
        .catch(() => this.showSnackbar('Failed to copy', 'error'))
    },

    formatDate (dateString: string) {
      return new Date(dateString).toLocaleString()
    },

    isExpired (dateString: string) {
      return new Date(dateString) < new Date()
    },

    updateItemsPerPage (value: number) {
      this.itemsPerPage = value
      this.page = 1
      this.loadActivationKeys()
    },

    showSnackbar (text: string, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },

    exportMultipleKeys (keys: Array<any>) {
      const rows = keys.map((key) => {
        const licenceConfig = key.licence_configurations
          ? key.licence_configurations.map((c: any) => `${c.quantity}x ${c.licence_type}`).join('; ')
          : ''
        return [key.activation_code, licenceConfig, key.duration_days, key.expires_at || 'Never']
      })
      const csvContent = [
        ['Activation Code', 'License Configurations', 'Duration (Days)', 'Expires At'].join(','),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))
      ].join('\n')
      this._downloadCsv(csvContent, `activation-keys-${Date.now()}.csv`)
      this.showSnackbar('Keys exported successfully', 'success')
    },

    _downloadCsv (content: string, filename: string) {
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.setAttribute('href', URL.createObjectURL(blob))
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
})
